import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Target, CheckCircle2 } from 'lucide-react';

interface AnalysisPhase {
  phase_number: number;
  title: string;
  objective: string;
  key_activities: string[];
  data_required: string[];
  expected_output: string;
  estimated_effort?: string;
}

interface RecommendedApproachSectionProps {
  phases: AnalysisPhase[];
  className?: string;
}

export const RecommendedApproachSection: React.FC<RecommendedApproachSectionProps> = ({
  phases,
  className = ''
}) => {
  const [expandedPhase, setExpandedPhase] = useState<number | null>(0);

  if (!phases || phases.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <Target className="w-5 h-5 text-primary-600" />
        <h3 className="text-lg font-semibold text-neutral-900">Recommended Approach</h3>
      </div>

      <div className="space-y-3">
        {phases.map((phase, index) => (
          <div
            key={phase.phase_number}
            className="border border-neutral-200 rounded-lg overflow-hidden"
          >
            {/* Phase Header */}
            <button
              onClick={() => setExpandedPhase(expandedPhase === index ? null : index)}
              className="w-full flex items-center justify-between p-4 bg-neutral-50 hover:bg-neutral-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-semibold text-sm">
                  {phase.phase_number}
                </span>
                <div className="text-left">
                  <p className="font-medium text-neutral-900">{phase.title}</p>
                  <p className="text-sm text-neutral-600">{phase.objective}</p>
                </div>
              </div>
              {expandedPhase === index ? (
                <ChevronUp className="w-5 h-5 text-neutral-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-neutral-500" />
              )}
            </button>

            {/* Phase Details */}
            {expandedPhase === index && (
              <div className="p-4 border-t border-neutral-200 space-y-4">
                {/* Key Activities */}
                {phase.key_activities && phase.key_activities.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-neutral-700 mb-2">Key Activities</p>
                    <ul className="space-y-1">
                      {phase.key_activities.map((activity, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-neutral-600">
                          <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{activity}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Data Required */}
                {phase.data_required && phase.data_required.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-neutral-700 mb-2">Data Required</p>
                    <div className="flex flex-wrap gap-2">
                      {phase.data_required.map((data, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                        >
                          {data}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Expected Output */}
                {phase.expected_output && (
                  <div>
                    <p className="text-sm font-medium text-neutral-700 mb-1">Expected Output</p>
                    <p className="text-sm text-neutral-600">{phase.expected_output}</p>
                  </div>
                )}

                {/* Estimated Effort */}
                {phase.estimated_effort && (
                  <div className="pt-2 border-t border-neutral-100">
                    <p className="text-xs text-neutral-500">
                      Estimated effort: {phase.estimated_effort}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecommendedApproachSection;
